import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { getOrderItemsFromMenuItems, useChangePersonalInfoOrderPoint } from '@/features';
import { getLocalizedReduxErrorMessage } from '@/shared/lib';
import {
  useFindClosestLocationMutation,
  useCalculateOrderPointDeliveryPriceMutation,
} from '@/entities/locations/api/locationsApi';
import { useAppSelector, useAppDispatch, setOrderPointError, setOrderInfoLoading } from '@/app/store';
import { DeliveryOptionEnum } from '@/entities';

export const useActualiseOrderPointBasket = (isOrderAnotherPerson?: boolean) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('validation');
  const { t: tError } = useTranslation('errors');
  const { orderPointDelivery, deliveryOption } = useAppSelector((state) => state.order);
  const { products, unavailableProductIds } = useAppSelector((state) => state.basket);
  const orderPointDisabled = useAppSelector((state) => state.application.orderPointDisabled);

  const { userOrderPoint, orderPointAddress } = orderPointDelivery;
  const [findClosestLocation] = useFindClosestLocationMutation();
  const [calculateDeliveryPrice] = useCalculateOrderPointDeliveryPriceMutation();

  const { onClearOrderPointLocationInfo, onSetOrderPointLocationInfo } =
    useChangePersonalInfoOrderPoint(isOrderAnotherPerson);

  const orderedItems = useMemo(
    () => getOrderItemsFromMenuItems(products, unavailableProductIds),
    [products, unavailableProductIds],
  );

  // Debounce the cart so rapid quantity changes coalesce into a single
  // delivery-price request instead of spamming the backend on every tick.
  const [debouncedOrderedItems] = useDebounce(orderedItems, 600);

  const actualiseLocation = useCallback(async () => {
    if (!userOrderPoint || orderPointDisabled || deliveryOption !== DeliveryOptionEnum.OrderPoint) return;

    try {
      dispatch(setOrderInfoLoading(true));
      dispatch(setOrderPointError(undefined));

      const data = await findClosestLocation({
        findClosestLocationDto: {
          latitude: userOrderPoint?.location.latitude,
          longitude: userOrderPoint?.location.longitude,
          isCDEKActive: true,
        },
      }).unwrap();

      const location = data.data;

      onClearOrderPointLocationInfo();

      if (!location) return;

      if (debouncedOrderedItems.length === 0) return;

      try {
        const priceData = await calculateDeliveryPrice({
          orderPointId: userOrderPoint.id,
          clientCalculateOrderPointDeliveryPriceDto: {
            locationId: location.id,
            orderedItems: debouncedOrderedItems,
          },
        }).unwrap();

        onSetOrderPointLocationInfo({
          locationId: location.id,
          deliveryPrice: priceData.data.price,
          minCalendarDays: priceData.data.minCalendarDays,
          maxCalendarDays: priceData.data.maxCalendarDays,
          paymentTypes: location.orderPoint?.paymentTypes,
          onlinePayment: location.orderPoint?.onlinePayment ?? undefined,
        });

        dispatch(setOrderPointError(undefined));
      } catch (priceError) {
        onClearOrderPointLocationInfo();

        // Surface the backend reason inline on the ordering page and keep the
        // order button disabled (via orderPointError) while the delivery price
        // cannot be calculated.
        dispatch(setOrderPointError(getLocalizedReduxErrorMessage(priceError ?? '', tError)));
      }
    } catch {
      onClearOrderPointLocationInfo();
      dispatch(setOrderPointError(t('delivery.invalidAddress')));
    } finally {
      dispatch(setOrderInfoLoading(false));
    }
  }, [
    t,
    tError,
    dispatch,
    deliveryOption,
    userOrderPoint,
    findClosestLocation,
    orderPointDisabled,
    debouncedOrderedItems,
    calculateDeliveryPrice,
    onSetOrderPointLocationInfo,
    onClearOrderPointLocationInfo,
  ]);

  useEffect(() => {
    actualiseLocation();
  }, [actualiseLocation, orderPointAddress, isOrderAnotherPerson]);
};
